import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Truck, LogOut, Lock, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Foundation Guide", to: "/" },
  { label: "Diagnostic", to: "/diagnostic" },
  { label: "Load Board", to: "/loadboard", paid: true },
  { label: "AI Coach", to: "/coach", paid: true },
  { label: "CPM Calc", to: "/cpm", paid: true },
  { label: "Paper Logs", to: "/paper-logs", paid: true },
  { label: "Export", to: "/export" },
];

export default function SiteHeader() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [pathname]);

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const isActive = (to) => (to === "/" ? pathname === "/" || pathname.startsWith("/step") : pathname.startsWith(to));

  const renderLink = (n, prefix, onClick) => {
    const active = isActive(n.to);
    const locked = n.paid && !user?.subscription_active;
    return (
      <Link
        key={n.to}
        to={n.to}
        onClick={onClick}
        data-testid={`${prefix}${n.label.toLowerCase().replace(/\s/g, "-")}`}
        className={`text-xs tracking-[0.15em] uppercase font-bold transition-colors inline-flex items-center gap-1 whitespace-nowrap shrink-0 ${
          active ? "text-brand" : "text-slate-400 hover:text-slate-900"
        }`}
      >
        {locked && <Lock className="w-3 h-3" />}
        {n.label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900 shrink-0">
          <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
          <span>RouteHaul<span className="hidden xl:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-4 flex-nowrap overflow-x-auto justify-end min-w-0">
          {NAV.map((n) => renderLink(n, "nav-"))}
          {user ? (
            <button onClick={doLogout} data-testid="logout-button" className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 hover:text-slate-900 inline-flex items-center gap-1 whitespace-nowrap shrink-0">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-xs tracking-[0.15em] uppercase font-bold text-white bg-brand hover:bg-brand-hover rounded-full px-4 py-2 transition-colors whitespace-nowrap shrink-0">
              Log in
            </Link>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white px-6 py-5 flex flex-col gap-5">
          {NAV.map((n) => renderLink(n, "mnav-", () => setOpen(false)))}
          {user ? (
            <button onClick={doLogout} data-testid="mobile-logout-button" className="text-xs tracking-[0.15em] uppercase font-bold text-slate-400 hover:text-slate-900 inline-flex items-center gap-1 self-start">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} data-testid="mnav-login" className="text-xs tracking-[0.15em] uppercase font-bold text-white bg-brand hover:bg-brand-hover rounded-full px-4 py-2 transition-colors self-start">
              Log in
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
